import React, { useEffect, useRef, useState } from "react";
import { FaVideo } from "react-icons/fa";

interface VideoThumbnailProps {
  videoUrl: string;
  onClick: () => void;
  className?: string;
  aspectRatio?: string;
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
  videoUrl,
  onClick,
  className = "",
  aspectRatio = "aspect-[4/5]",
}) => {
  const thumbnailVideoRef = useRef<HTMLVideoElement>(null);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);

  // Setup thumbnail video fallback
  useEffect(() => {
    if (!videoUrl) {
      setIsGeneratingThumbnail(false);
      return;
    }

    setIsGeneratingThumbnail(true);
    const thumbnailVideo = thumbnailVideoRef.current;
    if (!thumbnailVideo) return;

    const setupVideo = () => {
      thumbnailVideo.src = videoUrl;
      thumbnailVideo.currentTime = 1;
      thumbnailVideo.load();

      const handleSeeked = () => {
        console.log("Thumbnail generated");
        setIsGeneratingThumbnail(false);
        thumbnailVideo.pause();
      };

      const handleError = () => {
        console.error("Error loading video thumbnail");
        setIsGeneratingThumbnail(false);
      };

      thumbnailVideo.addEventListener("seeked", handleSeeked, { once: true });
      thumbnailVideo.addEventListener("error", handleError, { once: true });
    };

    setupVideo();
  }, [videoUrl]);

  return (
    <>
      {/* Thumbnail container */}
      <button
        type="button"
        onClick={onClick}
        className={`${aspectRatio} overflow-hidden relative group focus:outline-none ${className}`}
      >
        <video
          ref={thumbnailVideoRef}
          className={`w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105 ${
            isGeneratingThumbnail ? "invisible" : ""
          }`}
          muted
          playsInline
          preload="metadata"
          crossOrigin="anonymous"
        />
        {isGeneratingThumbnail && (
          <div className="absolute inset-0 w-full h-full bg-transparent flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {/* Video icon overlay - positioned at bottom left */}
        <div className="absolute bottom-2 left-2 z-10">
          <div className="bg-black/60 rounded-full p-1.5 flex items-center justify-center">
            <FaVideo className="text-white" size={12} />
          </div>
        </div>
      </button>
    </>
  );
};

export default VideoThumbnail;
